import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Guard zarządzający dostępem do modułu rozliczeń przedpłaconych.
 * Weryfikuje po stronie serwera API, czy aktualnie
 * zalogowany użytkownik ma odpowiednie uprawnienia.
 */
@Injectable({
  providedIn: 'root'
})
export class PrepaidAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) kodu modułu portfela prepaid.
   * Mechanizm ten chroni logikę biznesową rozliczeń przed pobraniem jej do przeglądarki
   * nieautoryzowanego użytkownika, wymuszając wcześniejszą walidację na endpoint'cie API.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku sukcesu autoryzacji).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/prepaid-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - prepaid - ok');
      return true;
    }));
  }

  /**
   * Weryfikuje uprawnienia przed wyrenderowaniem widoków związanych z zarządzaniem saldem.
   * Obsługuje błędy autoryzacji sesji (HTTP 401) poprzez bezpieczne wylogowanie użytkownika.
   * W przypadku braku odpowiedniej roli (np. HTTP 403), deleguje obsługę komunikatu
   * o odmowie dostępu do współdzielonej klasy CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/prepaid-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - prepaid - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
